/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import type { Workbook } from 'exceljs';
import * as Yup from 'yup';

// UI lib components
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Center,
  Container,
  Group,
  ScrollArea,
  Stack,
  Stepper,
  Table,
  Text,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';

// UI local components
import ResponsiveStepper from '@app/shared/ui-kit/molecules/ResponsiveStepper';
import ConfirmBar from '@app/shared/ui-kit/molecules/ConfirmBar';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { useWorkspaceUsers } from '@features/User/hooks/useWorkspaceUsers';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Assets
import {
  IconInfoCircle as IconValidationError,
  IconCheck,
  IconCloudUpload as IconUploadArea,
} from '@tabler/icons-react';

// Local API & Types
import {
  DialogImportQuestionProps,
  ValidationError,
  Colmun,
  ColumnKey,
  QuestionsTemplateRow,
} from './types';
import { ResourceMimeType } from '@app/types/blackroom-app/resourceMimeType';
import {
  CreateQuestionInput,
  QuestionPriority,
  QuestionResolutionStatus,
  useCreateQuestionMutation,
  useTopicsQuery,
} from '@app/types/blackroom-api';

// Styles
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import { palette } from '@app/shared/ui-kit/colors';
import { parseCell } from './utils/excel-js-extra';

// Constants
const MIN_STEP = 0;
const MAX_STEP = 1;
const TITLE_MAX_LENGTH = 1_000;
const DESCRIPTION_MAX_LENGTH = 1_000;
const GROUP_USER_EMAIL_SCHEMA = Yup.string().email();
const TOPICS_FORMAT_REGEX = /^[\w\s]+(,\s*[\w\s]+)*(,)?$/;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogImportQuestion(props: DialogImportQuestionProps) {
  /* ********************************** HOOKS ********************************* */

  // State
  const [currentStep, setCurrentStep] = useState(MIN_STEP);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [emptyFile, setEmptyfile] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );

  // Localization
  const { t } = useTranslation(['Common', 'Support']);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const workspace = useWorkspace();
  const { data: workspaceUsers } = useWorkspaceUsers();

  const refetchQuestions = useRefetchQuestions();

  // API
  const [createQuestions, { loading: isCreatingQuestions }] =
    useCreateQuestionMutation();
  const { data: topicsData } = useTopicsQuery({
    variables: { workspaceId: workspace.id },
  });

  const worksheetColmuns: Colmun[] = useMemo(
    () => [
      {
        key: 'title',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.title.label',
        ),
        width: 80,
      },
      {
        key: 'description',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.description.label',
        ),
        width: 80,
      },
      {
        key: 'priority',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.priority.label',
        ),
        width: 15,
      },
      {
        key: 'resolutionStatus',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.resolutionStatus.label',
        ),
        width: 15,
      },
      {
        key: 'topics',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.topics.label',
        ),
        width: 15,
      },
      {
        key: 'groupUserEmail',
        header: t(
          'Support:PageSupport.dialogImportQuestion.fileColmuns.groupUserEmail.label',
        ),
        width: 25,
      },
    ],
    [],
  );

  const topics = topicsData?.topics ?? [];

  const questionTempalteRows: QuestionsTemplateRow[] = useMemo(
    () => [
      {
        title: 'Example Question',
        description: 'This is an example description.',
        priority: QuestionPriority.Low,
        resolutionStatus: QuestionResolutionStatus.Closed,
        topics:
          topics.length > 0
            ? topics
                .slice(0, 2)
                .map((topic) => topic.name)
                .join(', ')
            : ['HR', 'IT', 'Finance'].join(', '),
        groupUserEmail: 'user@test.com',
      },
      {
        title: 'Example Question',
        description: 'This is an example description.',
        resolutionStatus: QuestionResolutionStatus.Open,
        priority: QuestionPriority.Medium,
      },
      {
        title: 'Example Question',
        priority: QuestionPriority.High,
      },
    ],
    [topics],
  );

  const validationTableHeaderColumns = useMemo(
    () => [
      t(
        'Support:PageSupport.dialogImportQuestion.validationTable.columns.rowNumber.label',
      ),
      t(
        'Support:PageSupport.dialogImportQuestion.validationTable.columns.columnHeader.label',
      ),
      t(
        'Support:PageSupport.dialogImportQuestion.validationTable.columns.message.label',
      ),
    ],
    [],
  );

  /* ******************************** CALLBACKS ******************************* */
  const nextStep = () =>
    setCurrentStep((current) => (current < MAX_STEP ? current + 1 : current));
  const prevStep = () =>
    setCurrentStep((current) => (current >= MIN_STEP ? current - 1 : current));

  const downloadTemplate = useCallback(async () => {
    const { Workbook } = await import('exceljs');
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Questions');
    worksheet.columns = worksheetColmuns;

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    questionTempalteRows.forEach((row) => worksheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: ResourceMimeType.xlsx,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'QuestionsTemplate.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  }, [worksheetColmuns]);

  const getColmunHeader = useCallback(
    (columnKey: ColumnKey) =>
      (worksheetColmuns.find(
        (worksheetColumn) => worksheetColumn.key === columnKey,
      )?.header as string) ?? '',
    [worksheetColmuns],
  );

  const isEmptyFile = useCallback(
    (workbook: Workbook) =>
      workbook.worksheets.every((worksheet) => worksheet.actualRowCount <= 1),
    [],
  );

  const handleValidateData = useCallback(async () => {
    if (uploadedFile) {
      if (emptyFile) setEmptyfile(false);
      const errors: ValidationError[] = [];
      const { Workbook } = await import('exceljs');
      const workbook = new Workbook();
      const data = await uploadedFile.arrayBuffer();
      await workbook.xlsx.load(data);

      if (isEmptyFile(workbook)) {
        setEmptyfile(true);
        nextStep();
        return;
      }

      workbook.worksheets.forEach((worksheet) => {
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber > 1) {
            const title = parseCell(row.getCell(1).text);
            const description = parseCell(row.getCell(2).text);
            const priority = parseCell(row.getCell(3).text);
            const resolutionStatus = parseCell(row.getCell(4).text);
            const topics = parseCell(row.getCell(5).text);
            const groupUserEmail = parseCell(row.getCell(6).text);

            if (!title) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('title'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.requiredValue',
                ),
              });
            }
            if (title && title.length > TITLE_MAX_LENGTH) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('title'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.maxLength',
                  {
                    valueMaxLength: TITLE_MAX_LENGTH,
                  },
                ),
              });
            }

            if (description && description.length > TITLE_MAX_LENGTH) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('description'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.maxLength',
                  {
                    valueMaxLength: DESCRIPTION_MAX_LENGTH,
                  },
                ),
              });
            }

            if (
              priority &&
              !Object.values(QuestionPriority).some(
                (p) => p.toLowerCase() === priority.toLowerCase(),
              )
            ) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('priority'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.InvalidValue',
                ),
              });
            }

            if (
              resolutionStatus &&
              !Object.values(QuestionResolutionStatus).some(
                (s) => s.toLowerCase() === resolutionStatus.toLowerCase(),
              )
            ) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('resolutionStatus'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.InvalidValue',
                ),
              });
            }

            if (
              topics &&
              topics.trim() &&
              !TOPICS_FORMAT_REGEX.test(topics.trim())
            ) {
              errors.push({
                rowNumber,
                columnHeader: getColmunHeader('topics'),
                message: t(
                  'Support:PageSupport.dialogImportQuestion.validationTable.errors.InvalidValue',
                ),
              });
            }

            if (groupUserEmail) {
              const validGroupUserEmail =
                GROUP_USER_EMAIL_SCHEMA.isValidSync(groupUserEmail);

              if (!validGroupUserEmail) {
                errors.push({
                  rowNumber,
                  columnHeader: getColmunHeader('groupUserEmail'),
                  message: t(
                    'Support:PageSupport.dialogImportQuestion.validationTable.errors.InvalidValue',
                  ),
                });
              }

              const user = workspaceUsers.find(
                (workspaceUser) =>
                  workspaceUser.email.toLowerCase() ===
                  groupUserEmail.toLowerCase(),
              );

              if (validGroupUserEmail && !user) {
                errors.push({
                  rowNumber,
                  columnHeader: getColmunHeader('groupUserEmail'),
                  message: t(
                    'Support:PageSupport.dialogImportQuestion.validationTable.errors.userNotFound',
                  ),
                });
              }
            }
          }
        });
      });

      if (errors.length > 0) {
        setValidationErrors(errors);
      } else {
        setValidationErrors([]);
      }
      nextStep();
    }
  }, [uploadedFile, emptyFile]);

  const handleCreateQuestions = useCallback(async () => {
    if (uploadedFile && validationErrors.length === 0 && !emptyFile) {
      const { Workbook } = await import('exceljs');
      const workbook = new Workbook();
      const data = await uploadedFile.arrayBuffer();
      await workbook.xlsx.load(data);
      const questionsToCreate: CreateQuestionInput['questionsInput'] = [];

      workbook.worksheets.forEach((worksheet) => {
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber > 1) {
            const title = parseCell(row.getCell(1).text);
            const description = parseCell(row.getCell(2).text) || undefined;

            const priorityFromCell = parseCell(row.getCell(3).text);
            const priority =
              Object.values(QuestionPriority).find(
                (p) =>
                  priorityFromCell &&
                  p.toLowerCase() === priorityFromCell.toLowerCase(),
              ) || QuestionPriority.Low;

            const resolutionStatusFromCell = row.getCell(4).text;
            const resolutionStatus =
              Object.values(QuestionResolutionStatus).find(
                (s) =>
                  resolutionStatusFromCell &&
                  s.toLowerCase() === resolutionStatusFromCell.toLowerCase(),
              ) || undefined;

            const topicNames = parseCell(row.getCell(5).text)
              .split(',')
              .map((topic) => topic.trim())
              .filter((topic) => topic.length > 0);
            const topicIds = topicNames
              .map((name) =>
                topics.find((t) => t.name.toLowerCase() === name.toLowerCase()),
              )
              .filter((t) => t !== undefined)
              .map((t) => t.id);
            const groupUserEmail =
              parseCell(row.getCell(6).text).toLocaleLowerCase() || undefined;

            questionsToCreate.push({
              title,
              description,
              priority,
              resolutionStatus,
              topicIds,
              groupUserEmail,
            });
          }
        });
      });

      try {
        await createQuestions({
          variables: {
            createQuestionInput: {
              workspaceId: workspace.id,
              questionsInput: questionsToCreate,
            },
          },
        });
        refetchQuestions();
        showNotification({
          title: t(
            'Support:PageSupport.dialogImportQuestion.successNotification.title',
          ),
          message: t(
            'Support:PageSupport.dialogImportQuestion.successNotification.description',
          ),
          color: 'blackroomGreen',
        });
        dialogStore?.closeDialog?.('dialog-import-question');
      } catch (error) {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
        console.error(error);
      }
    }
  }, [uploadedFile, validationErrors, emptyFile, workspace, topics]);

  /* **************************** RENDERING HELPERS *************************** */

  const renderAlert = () => {
    if (emptyFile) {
      return (
        <Alert
          variant="light"
          color="blackroomRed"
          title={t(
            'Support:PageSupport.dialogImportQuestion.alerts.emptyFile.title',
          )}
          icon={<IconValidationError />}
          p="xs"
        >
          {t(
            'Support:PageSupport.dialogImportQuestion.alerts.emptyFile.description',
          )}
        </Alert>
      );
    }

    if (validationErrors.length > 0) {
      return (
        <Alert
          variant="light"
          color="blackroomRed"
          title={t(
            'Support:PageSupport.dialogImportQuestion.alerts.error.title',
          )}
          icon={<IconValidationError />}
          p="xs"
        >
          {t(
            'Support:PageSupport.dialogImportQuestion.alerts.error.description',
          )}
        </Alert>
      );
    }

    return (
      <Alert
        variant="light"
        color="blackroomGreen"
        title={t(
          'Support:PageSupport.dialogImportQuestion.alerts.success.title',
        )}
        icon={<IconCheck />}
        p="xs"
      >
        {t(
          'Support:PageSupport.dialogImportQuestion.alerts.success.description',
        )}
      </Alert>
    );
  };

  /* ******************************** RENDERING ******************************* */

  return (
    <Container w={600} fluid>
      <Box px="md" pb="md">
        <ResponsiveStepper active={currentStep}>
          <Stepper.Step
            label={t(
              'Support:PageSupport.dialogImportQuestion.steps.uploadFile.label',
            )}
          >
            <Dropzone
              accept={[ResourceMimeType.xlsx]}
              multiple={false}
              onDrop={(files) => {
                setUploadedFile(files[0]);
              }}
              maxSize={5 * 1024 ** 2}
            >
              <Center h="15vh">
                <Stack justify="center" align="center" gap="xs">
                  <ActionIcon
                    variant="transparent"
                    color="blackroomBlue"
                    aria-label={t('Common:cta.upload')}
                  >
                    <IconUploadArea />
                  </ActionIcon>
                  <Text size="sm" fw={FONT_WEIGHT.lighter} ta="center">
                    {t(
                      'Support:PageSupport.dialogImportQuestion.steps.uploadFile.dropzonePlaceholder',
                    )}
                  </Text>
                </Stack>
              </Center>
            </Dropzone>
            {uploadedFile && (
              <Group gap={2} wrap="nowrap">
                <IconCheck
                  size={ICON_SIZE_MAP.small}
                  color={palette.blackroomGreen}
                />
                <Text c="blackroomGreen" pt={4} fz="sm" truncate>
                  {t(
                    'Support:PageSupport.dialogImportQuestion.steps.uploadFile.selectedFilePrefix',
                  )}{' '}
                  {uploadedFile.name}
                </Text>
              </Group>
            )}
            <Group pt="xs" justify="flex-end">
              <Button
                onClick={downloadTemplate}
                py={0}
                px={2}
                h="fit-content"
                variant="transparent"
                color="blackroomGrey"
                fz="sm"
                fw={300}
                style={{
                  textDecoration: 'underline',
                }}
              >
                {t(
                  'Support:PageSupport.dialogImportQuestion.steps.uploadFile.ctaDownloadTemplate',
                )}
              </Button>
            </Group>
          </Stepper.Step>
          <Stepper.Step
            label={t(
              'Support:PageSupport.dialogImportQuestion.steps.validateData.label',
            )}
          >
            {validationErrors.length > 0 && (
              <Stack gap="sm">
                <ScrollArea.Autosize w="100%" mah={300}>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        {validationTableHeaderColumns.map((column, index) => (
                          <Table.Th>{column}</Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {validationErrors.map((error, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>{error.rowNumber}</Table.Td>
                          <Table.Td>{error.columnHeader}</Table.Td>
                          <Table.Td>{error.message}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea.Autosize>
              </Stack>
            )}
            {renderAlert()}
          </Stepper.Step>
        </ResponsiveStepper>
      </Box>
      <ConfirmBar
        labelProps={{
          confirm:
            currentStep === MAX_STEP
              ? t('Common:cta.save')
              : t('Common:cta.saveContinue'),
          cancel:
            currentStep === MIN_STEP
              ? t('Common:cta.cancel')
              : t('Common:cta.back'),
        }}
        onConfirm={() => {
          if (uploadedFile && currentStep === MIN_STEP) {
            handleValidateData();
            return;
          }

          if (
            uploadedFile &&
            currentStep === MAX_STEP &&
            validationErrors.length === 0 &&
            !emptyFile
          ) {
            handleCreateQuestions();
          }
        }}
        onCancel={() => {
          currentStep > MIN_STEP
            ? prevStep()
            : dialogStore?.closeDialog?.('dialog-import-question');
        }}
        disabled={
          (currentStep === MIN_STEP && !uploadedFile) ||
          (currentStep === MAX_STEP &&
            (validationErrors.length > 0 || emptyFile)) ||
          isCreatingQuestions
        }
        saving={isCreatingQuestions}
      />
    </Container>
  );
}

export default DialogImportQuestion;
